/****************************************************
 * Module Name:  tableIncMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import TableIncProxy from "../../utility/proxy/manager/tableIncProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CommonSnackbar from "../common/commonSnackBar";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import AddTableIncModal from "./addTableIncModal";
import PageTitle from "../common/pageTitle";

const TableIncMaster = () => {

    const local_Permission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MASTER_TABLEINC);

    const [data, setData] = useState([]);
    const [showAddTableIncModal, setShowAddTableIncModal] = useState(false);
    const [editOperation, setEditOperation] = useState(false);
    const [loader, setLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [sourceData, setSourceData] = useState({});
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }

    useEffect(() => {
        PageTitle("MASTER_TABLEINC");
        if (Utility.isReadAllowed(local_Permission)) {
            handleSearch();
        }
    }, []);

    const handleSearch = () => {
        setLoader(true);
        TableIncProxy.GetAllTableInc(response => {
            if (response) {
                const uniqueFilterValues = {
                    name: Utility.getDistinctOptions(response, "name"),
                    fullname: Utility.getDistinctOptions(response, "fullname")
                };
                setFilterValues(uniqueFilterValues);
                setData(response.sort((a, b) => a.seq - b.seq));
                setLoader(false);
            } else {
                setLoader(false);
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
            }
        });
    }

    const addTableInc = (data) => {
        setLoader(true);
        if (editOperation) {
            TableIncProxy.UpdateTableInc(data, response => {
                if (response) {
                    setLoader(false);
                    setShowAddTableIncModal(false);
                    handleSearch();
                } else {
                    setLoader(false);
                    setShowAddTableIncModal(false);
                    setSnackbar(true, Constants.APiFailErrorMessage, "error");
                }
            })
        } else {
            TableIncProxy.AddTableInc(data, response => {
                if (response) {
                    setLoader(false);
                    setShowAddTableIncModal(false);
                    handleSearch();
                } else {
                    setLoader(false);
                    setShowAddTableIncModal(false);
                    setSnackbar(true, Constants.APiFailErrorMessage, "error");
                }
            })
        }
    }

    const initialColumns = () => [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            type: 'number',
            align: "center",
        },
        {
            field: "name",
            headerName: "TABLE INC",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.name : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "fullname",
            headerName: "FULLNAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fullname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "seq",
            headerName: "SEQUENCE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: 'number',
            renderCell: renderCellExpand,
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setShowAddTableIncModal(true);
                            setEditOperation(true);
                            setSourceData(params.row);
                        }}
                    >
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    const columns = Utility.isUpdateAllowed(local_Permission) ? initialColumns() :
        initialColumns().filter((col) => col.field !== "edit");

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {loader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => handleSearch()}
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}

                {Utility.isAddAllowed(local_Permission) ? (
                    <Tooltip title="Add Table Inc">
                        <Button
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ ml: "5px" }}
                            onClick={() => {
                                setShowAddTableIncModal(true);
                                setEditOperation(false);
                                setSourceData(null);
                            }}
                            startIcon={<AddBoxOutlinedIcon size="large" />}
                        >
                            Table Inc
                        </Button>
                    </Tooltip>
                ) : null}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }

    return (
        <Box>
            {Utility.isReadAllowed(local_Permission) ? (
                <Box>

                    <CommonSnackbar
                        show={showSnackbar}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        handleClose={() => this.setState({ errorSnackbar: false })}
                        title={snackbarVariant === "success" ? "Success" :
                            snackbarVariant === "error" ? "Failed" : "Warning"}
                    />

                    {showAddTableIncModal ? (
                        <AddTableIncModal
                            handleSave={addTableInc}
                            handleClose={() => setShowAddTableIncModal(false)}
                            editOperation={editOperation}
                            sourceData={sourceData}
                            loader={loader}
                        />
                    ) : null}

                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            marginTop: 2,
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "84vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },
                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            slots={{ toolbar: CustomToolbar }}
                            density="compact"
                            rows={data}
                            columns={columns}
                            getRowId={(row) => row.id}
                            loading={loader}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                        id: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </Box>
            ) : null}
        </Box>
    )

}

export default TableIncMaster;